import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  message,
  Typography,
  Avatar,
  Row,
  Col
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import userService from '../../services/userService';
import { useUser } from '../../context/UserContext';

const { Option } = Select;
const { Title, Text } = Typography;

const AddAdmin = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);

  // User role options
  const userRoles = [
    { value: 3, label: 'Admin' },
    { value: 4, label: 'Data Entry' },
    { value: 5, label: 'Excel Data' },
    { value: 6, label: 'Client' },
    { value: 7, label: 'Report Viewer' },
    { value: 8, label: 'User Management' }
  ];

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      console.log('Submitting admin data:', values);
      const response = await userService.addAdmin(values);
      console.log('API Response:', response);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Admin added successfully!');
        form.resetFields();
        navigate('/users/user');
      } else {
        message.error(response.data.message || 'Failed to add admin');
      }
    } catch (error) {
      console.error('Error adding admin:', error);
      message.error('Failed to add admin');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
  };

  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Handle number input (only allow numbers)
  const handleNumberInput = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 && // backspace
      charCode !== 46 && // delete
      charCode !== 37 && // left arrow
      charCode !== 39 && // right arrow
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Admin User</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Admin"
      desc="Create a new admin user for the system"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="firstname"
                rules={[
                  { required: true, message: 'Please enter first name' },
                  { min: 2, message: 'First name must be at least 2 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter first name"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="lastname"
                rules={[
                  { required: true, message: 'Please enter last name' },
                  { min: 2, message: 'Last name must be at least 2 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter last name"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="user_email"
                rules={[
                  { required: true, message: 'Please provide email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input 
                  type="email"
                  placeholder="Enter email address"
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Mobile Number"
                name="user_number"
                rules={[
                  { required: true, message: 'Please enter mobile number' },
                  { len: 10, message: 'Mobile number must be exactly 10 digits' },
                  { pattern: /^[0-9]+$/, message: 'Mobile number must contain only numbers' }
                ]}
              >
                <Input 
                  type="number"
                  placeholder="Enter 10-digit mobile number"
                  minLength={10}
                  maxLength={10}
                  onKeyPress={handleNumberInput}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Password"
                name="user_password"
                rules={[
                  { required: true, message: 'Please provide password' },
                  { min: 6, message: 'Password must be at least 6 characters' }
                ]}
              >
                <Input.Password 
                  placeholder="Enter password"
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="User Role"
                name="user_role_id"
                rules={[
                  { required: true, message: 'Please select a role' }
                ]}
              >
                <Select 
                  placeholder="Select user role"
                  showSearch
                  optionFilterProp="children"
                >
                  {userRoles.map(role => (
                    <Option key={role.value} value={role.value}>
                      {role.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Form Actions */}
          <Form.Item style={{ marginTop: '32px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Add Admin
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>
              
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </PageLayout>
  );
};

export default AddAdmin;